// apps/web/src/router/routes.ts
// TanStack Router v1 route tree per Plan §4.6.9 + Brief §10 (post-Brief-Revision:
// 6 routes including /sign-in + /__primitives debug). Step 22.B: placeholder
// components; Step 22.D adds <RequireAuth/> beforeLoad guard on protected
// routes; Step 24.C swaps /brief to lazy-loaded AdBrief composite tree
// (per Plan §4.6.7 lazy-load policy keeps Recharts out of initial chunk).

import { lazy, Suspense } from 'react';
import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';

import App from '../App.js';
import { requireAuth }       from '../auth/require-auth.js';
import { Landing }           from '../pages/landing.js';
import { SignInScreen }      from '../auth/SignInScreen.js';
import { briefSearch, trackerSearch, opsLabSearch, dataOracleSearch } from './searchParams.js';

// Code-split per Plan §4.6.7: heavy dashboards lazy-load so /sign-in
// entrypoint stays under §4.6.2 ~200 KiB gzip budget. Each dashboard
// owns its chunk (recharts/TanStack-Table only loaded when needed).
const LoadingFallback = (
  <div style={{ padding: 64, textAlign: 'center', color: 'var(--color-text-3)' }}>Loading dashboard…</div>
);

const AdBriefLazy = lazy(() =>
  import('../dashboards/ad-brief/index.js').then((m) => ({ default: m.AdBrief })),
);
function AdBriefRoute(): JSX.Element {
  return <Suspense fallback={LoadingFallback}><AdBriefLazy /></Suspense>;
}

const AdAmmLazy = lazy(() =>
  import('../dashboards/ad-amm/index.js').then((m) => ({ default: m.AdAmm })),
);
function AdAmmRoute(): JSX.Element {
  return <Suspense fallback={LoadingFallback}><AdAmmLazy /></Suspense>;
}

const AdSignalTrackerLazy = lazy(() =>
  import('../dashboards/ad-signal-tracker/index.js').then((m) => ({ default: m.AdSignalTracker })),
);
function AdSignalTrackerRoute(): JSX.Element {
  return <Suspense fallback={LoadingFallback}><AdSignalTrackerLazy /></Suspense>;
}

const AdOpsLabLazy = lazy(() =>
  import('../dashboards/ad-ops-lab/index.js').then((m) => ({ default: m.AdOpsLab })),
);
function AdOpsLabRoute(): JSX.Element {
  return <Suspense fallback={LoadingFallback}><AdOpsLabLazy /></Suspense>;
}

const DataOracleLazy = lazy(() =>
  import('../dashboards/data-oracle/index.js').then((m) => ({ default: m.DataOracle })),
);
function DataOracleRoute(): JSX.Element {
  return <Suspense fallback={LoadingFallback}><DataOracleLazy /></Suspense>;
}

const rootRoute = createRootRoute({ component: App });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path:           '/',
  beforeLoad:     requireAuth,
  component:      Landing,
});

const briefRoute = createRoute({
  getParentRoute: () => rootRoute,
  path:           '/brief',
  beforeLoad:     requireAuth,
  validateSearch: (search: Record<string, unknown>) => briefSearch.parse(search),
  component:      AdBriefRoute,
});

// 2026-05-25 — URL rename per user direction: paths now match the
// user-facing dashboard names (matches landing.tsx ENTRIES labels +
// AD_*_MANIFEST.title strings). /brief stays because it's already the
// Daily Brief. The route VARIABLE names + component names + dirs
// (ammRoute/AdAmm/ad-amm, etc.) keep their legacy identifiers — internal
// only, no user-facing effect.
//
// 2026-06-09 — "LP Pools" renamed to "Crypto" per user direction: path
// → /crypto, label/manifest-title → "Crypto", DashId → 'crypto'.
// Internal ammRoute/AdAmm/ad-amm identifiers unchanged. (The temporary
// old→new 302 in apps/web/public/_redirects was removed 2026-06-26 once the
// cutover settled.)
const ammRoute = createRoute({
  getParentRoute: () => rootRoute,
  path:           '/crypto',
  beforeLoad:     requireAuth,
  component:      AdAmmRoute,
});

const signalTrackerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path:           '/signal-history',
  beforeLoad:     requireAuth,
  validateSearch: (search: Record<string, unknown>) => trackerSearch.parse(search),
  component:      AdSignalTrackerRoute,
});

const opsLabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path:           '/operations',
  beforeLoad:     requireAuth,
  validateSearch: (search: Record<string, unknown>) => opsLabSearch.parse(search),
  component:      AdOpsLabRoute,
});

const oracleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path:           '/oracle',
  beforeLoad:     requireAuth,
  validateSearch: (search: Record<string, unknown>) => dataOracleSearch.parse(search),
  component:      DataOracleRoute,
});

// signInRoute is intentionally UNGUARDED — landing page for un-auth users
// redirected here by the requireAuth guard on the 5 protected routes.
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path:           '/sign-in',
  component:      SignInScreen,
});

// Plan §4.9.9 Step 23 A3 — visual smoke catalog. Retained as a primitives
// debug surface (not production-facing). audit-r3 F-017 (2026-05-24):
// added requireAuth so authenticated users only — the prior unguarded
// posture leaked tone tokens + sample data to any caller with the URL.
// 2026-05-27 (audit-2026-05-27 LOW): DEV-only + lazy-imported so the
// debug surface drops from the prod bundle (Vite tree-shakes the
// `import.meta.env.DEV` branch).
const DebugPrimitivesLazy = lazy(() =>
  import('../pages/debug-primitives.js').then((m) => ({ default: m.DebugPrimitives })),
);
function DebugPrimitivesRoute(): JSX.Element {
  return <Suspense fallback={LoadingFallback}><DebugPrimitivesLazy /></Suspense>;
}
const debugPrimitivesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path:           '/__primitives',
  beforeLoad:     requireAuth,
  component:      DebugPrimitivesRoute,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  briefRoute,
  ammRoute,
  signalTrackerRoute,
  opsLabRoute,
  oracleRoute,
  signInRoute,
  ...(import.meta.env.DEV ? [debugPrimitivesRoute] : []),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
